<template>
  <div class="flex items-center gap-1 flex-shrink-0">
    <!-- Portal -->
    <div class="filter-item">
      <select v-model="local.portal" @change="emitChange" aria-label="Portal" class="filter-chip appearance-none cursor-pointer">
        <option value="">All Portals</option>
        <option v-for="p in portals" :key="p.code" :value="p.code">{{ p.name }}</option>
      </select>
      <ChevronDownIcon class="filter-chevron" aria-hidden="true" />
    </div>

    <div class="filter-divider" />

    <!-- Status -->
    <div class="filter-item">
      <select v-model="local.status" @change="emitChange" aria-label="Status" class="filter-chip appearance-none cursor-pointer">
        <option value="">All Statuses</option>
        <option value="active">Active</option>
        <option value="closed">Closed</option>
      </select>
      <ChevronDownIcon class="filter-chevron" aria-hidden="true" />
    </div>

    <div class="filter-divider" />

    <!-- Type (the portal's own list: Live, Awarded, Under Evaluation...). Only
         shown once the facets are loaded and the portal in view actually has
         more than one, so it never appears as a dropdown with nothing to pick. -->
    <template v-if="orderedTypes.length > 1">
      <div class="filter-item">
        <select
          v-model="local.listing_type"
          @change="emitChange"
          aria-label="Tender type"
          class="filter-chip appearance-none cursor-pointer"
        >
          <option value="">All Types</option>
          <option v-for="t in orderedTypes" :key="t.value" :value="t.value">
            {{ listingTypeLabel(t.value) }} ({{ t.count }})
          </option>
        </select>
        <ChevronDownIcon class="filter-chevron" aria-hidden="true" />
      </div>

      <div class="filter-divider" />
    </template>

    <!-- Closing date — the bare date input gave no hint of what it filtered -->
    <label class="filter-item cursor-pointer">
      <CalendarIcon class="w-3.5 h-3.5 text-slate-500 flex-shrink-0" aria-hidden="true" />
      <span class="filter-label">Closing Before</span>
      <input
        v-model="local.closing_before"
        @change="emitChange"
        type="date"
        class="filter-chip cursor-pointer"
      />
    </label>

    <div class="filter-divider" />

    <!-- Sort -->
    <div class="filter-item">
      <Bars3BottomLeftIcon class="w-3.5 h-3.5 text-slate-500 flex-shrink-0" aria-hidden="true" />
      <select v-model="local.sort_by" @change="emitChange" aria-label="Sort" class="filter-chip appearance-none cursor-pointer">
        <option value="published_date">Sort: Newest</option>
        <option value="closing_date">Sort: Closing Date</option>
        <option value="title">Sort: Title (A-Z)</option>
      </select>
      <ChevronDownIcon class="filter-chevron" aria-hidden="true" />
    </div>

    <!-- Clear -->
    <button
      v-if="hasFilters"
      @click="clear"
      class="filter-chip text-slate-600 hover:text-slate-900 flex items-center gap-1 transition-colors ml-1"
    >
      <XMarkIcon class="w-3.5 h-3.5" aria-hidden="true" />
      Clear Filters
    </button>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { ChevronDownIcon, XMarkIcon, CalendarIcon, Bars3BottomLeftIcon } from "@heroicons/vue/24/outline";
import { listingTypeLabel, orderFacets } from "../utils/listingType";

const props = defineProps({
  modelValue:   { type: Object, default: () => ({}) },
  portals:      { type: Array,  default: () => [] },
  // Facet rows [{value, count}] for listing_type, already scoped to the
  // selected portal by the parent.
  listingTypes: { type: Array,  default: () => [] },
});

const emit = defineEmits(["update:modelValue", "change"]);

const local = ref({
  portal:         "",
  status:         "",
  listing_type:   "",
  closing_before: "",
  sort_by:        "published_date",
  ...props.modelValue,
});

watch(() => props.modelValue, (val) => {
  local.value = { ...local.value, ...val };
}, { deep: true });

// Biddable types first, then the rest — the API orders by count alone, which
// buries "Live" under thousands of awarded contracts.
const orderedTypes = computed(() => orderFacets(props.listingTypes));

// A selected type that the current portal does not publish would filter the
// list down to nothing with no way to see why, so it is dropped as soon as the
// offered values change.
watch(orderedTypes, (rows) => {
  const chosen = local.value.listing_type;
  if (chosen && !rows.some((r) => r.value === chosen)) {
    local.value.listing_type = "";
    emitChange();
  }
});

const hasFilters = computed(() =>
  local.value.portal || local.value.status ||
  local.value.listing_type || local.value.closing_before
);

function emitChange() {
  emit("update:modelValue", { ...local.value });
  emit("change", { ...local.value });
}

function clear() {
  local.value = {
    portal: "", status: "", listing_type: "", closing_before: "",
    sort_by: "published_date",
  };
  emitChange();
}
</script>

<style scoped>
.filter-item {
  display: flex;
  align-items: center;
  gap: 2px;
  position: relative;
}

.filter-chip {
  font-size: 0.8rem;
  font-weight: 500;
  color: #475569;
  background: transparent;
  border: none;
  outline: none;
  padding: 0.3rem 0.4rem;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.filter-chip:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.filter-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #64748b;
  white-space: nowrap;
  padding-left: 0.2rem;
}

.filter-chevron {
  width: 12px;
  height: 12px;
  color: #64748b;
  flex-shrink: 0;
  pointer-events: none;
}

.filter-divider {
  width: 1px;
  height: 16px;
  background: #e2e8f0;
  flex-shrink: 0;
  margin: 0 4px;
}
</style>
